import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ToolsManagement.css';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

const ModernErrorMessage = ({ error, onClose }) => {
  if (!error) return null;

  return (
    <div className="error-message">
      <div className="error-content">
        <span className="error-icon">⚠️</span>
        <p className="error-text">{error}</p>
        <button className="error-close" onClick={onClose}>×</button>
      </div>
    </div>
  );
};

const ToolsManagement = () => {
  const [tools, setTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSchemas, setExpandedSchemas] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    tool_name: '',
    description: '',
    parameters_json_schema: '',
    backend_implementation_ref: '',
    required_auth_service_name: ''
  });

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch tools directly from the tools endpoint (tools are global)
      const response = await axios.get('/api/agents/tools', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setTools(response.data.tools || []);
    } catch (err) {
      setError('Failed to fetch tools. Make sure you have admin permissions.');
      console.error('Error fetching tools:', err);
      setTools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTool = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Parse schema if it's a string
      if (typeof submitData.parameters_json_schema === 'string') {
        submitData.parameters_json_schema = JSON.parse(submitData.parameters_json_schema);
      }

      await axios.post('/api/tools', submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setShowCreateModal(false);
      resetForm();
      fetchTools();
      setError(null);
    } catch (err) {
      setError('Failed to create tool');
      console.error('Error creating tool:', err);
    }
  };

  const handleEditTool = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Parse schema if it's a string
      if (typeof submitData.parameters_json_schema === 'string') {
        submitData.parameters_json_schema = JSON.parse(submitData.parameters_json_schema);
      }

      await axios.put(`/api/tools/${selectedTool.tool_definition_id}`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setShowEditModal(false);
      resetForm();
      fetchTools();
      setError(null);
    } catch (err) {
      setError('Failed to update tool');
      console.error('Error updating tool:', err);
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (window.confirm('Are you sure you want to delete this tool?')) {
      try {
        await axios.delete(`/api/tools/${toolId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        fetchTools();
        setError(null);
      } catch (err) {
        setError('Failed to delete tool');
        console.error('Error deleting tool:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tool_name: '',
      description: '',
      parameters_json_schema: '',
      backend_implementation_ref: '',
      required_auth_service_name: ''
    });
    setSelectedTool(null);
  };

  const openEditModal = (tool) => {
    setSelectedTool(tool);
    setFormData({
      tool_name: tool.tool_name || '',
      description: tool.description || '',
      parameters_json_schema: typeof tool.parameters_json_schema === 'string' 
        ? tool.parameters_json_schema 
        : JSON.stringify(tool.parameters_json_schema, null, 2),
      backend_implementation_ref: tool.backend_implementation_ref || '',
      required_auth_service_name: tool.required_auth_service_name || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = (tool) => {
    setSelectedTool(tool);
    setShowViewModal(true);
  };

  const toggleSchema = (toolId) => {
    setExpandedSchemas(prev => ({
      ...prev,
      [toolId]: !prev[toolId]
    }));
  };

  // Helper function to safely count parameters
  const getParameterCount = (schema) => {
    try {
      if (!schema) return 0;

      let parsedSchema = schema;

      // Handle double-encoded JSON strings from the API
      if (typeof schema === 'string') {
        // First parse to get the actual JSON string
        parsedSchema = JSON.parse(schema);

        // If it's still a string, parse again (double-encoded)
        if (typeof parsedSchema === 'string') {
          parsedSchema = JSON.parse(parsedSchema);
        }
      }

      // Count properties
      return Object.keys(parsedSchema?.properties || {}).length;
    } catch (error) {
      console.warn('Error parsing schema for parameter count:', error);
      return 0;
    }
  };

  const copyToClipboard = (text, toolId) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show copied feedback
      setTimeout(() => {}, 2000);
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const filteredTools = (tools || []).filter(tool =>
    tool?.tool_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool?.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <MainLayout title="Tools Management">
        <div className="tools-container">
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <div className="loading-text">
              <span className="loading-icon">🔧</span>
              <p>Loading Tools Management...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Tools Management">
      <div className="tools-management-container">
      {/* Header Section */}
      <div className="tools-header">
        <div className="header-content">
          <div className="header-text">
            <h1>🔧 Tools Management</h1>
            <p>Manage and configure AI tools and functions</p>
          </div>
          <button
            className="create-tool-btn"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            <span>➕</span>
            Create New Tool
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        <div className="tools-count">
          <span className="count-badge">{filteredTools.length} tools</span>
        </div>
      </div>

      {/* Error Message */}
      <ModernErrorMessage
        error={error}
        onClose={() => setError(null)}
      />

      {/* Tools Grid */}
      <div className="tools-grid">
        {filteredTools.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔧</div>
            <h3>No tools found</h3>
            <p>No tools match your search criteria</p>
          </div>
        ) : (
          filteredTools.map(tool => (
            <div key={tool.tool_definition_id} className="tool-card">
              <div className="tool-card-header">
                <div className="tool-info">
                  <h3>{tool.tool_name}</h3>
                  <p className="tool-description">{tool.description}</p>
                </div>
                <div className="tool-actions">
                  <button
                    className="action-btn view-btn"
                    onClick={() => openViewModal(tool)}
                    title="View Tool"
                  >
                    👁️
                  </button>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => openEditModal(tool)}
                    title="Edit Tool"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteTool(tool.tool_definition_id)}
                    title="Delete Tool"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="tool-details">
                <div className="detail-row">
                  <span className="detail-label">Implementation:</span>
                  <code className="detail-value implementation-text" title={tool.backend_implementation_ref}>
                    {tool.backend_implementation_ref && tool.backend_implementation_ref.length > 50 
                      ? `${tool.backend_implementation_ref.substring(0, 50)}...` 
                      : tool.backend_implementation_ref}
                  </code>
                </div>
                {tool.required_auth_service_name && (
                  <div className="detail-row auth-service-row">
                    <span className="detail-label">Auth Service:</span>
                    <span className="auth-badge" title={tool.required_auth_service_name}>
                      {tool.required_auth_service_name.length > 25 
                        ? `${tool.required_auth_service_name.substring(0, 25)}...` 
                        : tool.required_auth_service_name}
                    </span>
                  </div>
                )}
              </div>

              <div className="tool-schema-section">
                <div className="schema-header">
                  <span className="schema-label">Parameters Schema</span>
                  <div className="schema-actions">
                    <button
                      className="schema-copy-btn"
                      onClick={() => copyToClipboard(
                        JSON.stringify(tool.parameters_json_schema, null, 2),
                        tool.tool_definition_id
                      )}
                      title="Copy Schema"
                    >
                      📋
                    </button>
                    <button
                      className="schema-toggle-btn"
                      onClick={() => toggleSchema(tool.tool_definition_id)}
                      title="Toggle Schema"
                    >
                      {expandedSchemas[tool.tool_definition_id] ? '▼' : '▶'}
                    </button>
                  </div>
                </div>

                {expandedSchemas[tool.tool_definition_id] ? (
                  <div className="schema-display">
                    <pre>{JSON.stringify(tool.parameters_json_schema, null, 2)}</pre>
                  </div>
                ) : (
                  <div className="schema-preview">
                    <span className="schema-preview-text">
                      {getParameterCount(tool.parameters_json_schema)} parameters defined
                    </span>
                  </div>
                )}
              </div>

              <div className="tool-footer">
                <span className="tool-date">
                  Updated: {formatDate(tool.updated_at)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Tool Modal */}
      {showViewModal && selectedTool && (
        <div className="modal-overlay modern-modal" onClick={() => setShowViewModal(false)}>
          <div className="modal-content tool-view-modal enhanced-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header gradient-header">
              <div className="header-title-section">
                <div className="header-text">
                  <h2>Tool Details - {selectedTool.tool_name}</h2>
                </div>
              </div>
              <button className="close-modal modern-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>

            <div className="tool-view-content clean-content">
              <div className="view-section">
                <h3 className="section-title">Basic Information</h3>
                <div className="info-fields">
                  <div className="info-field">
                    <span className="field-label">TOOL NAME:</span>
                    <div className="field-value">{selectedTool.tool_name}</div>
                  </div>
                  <div className="info-field">
                    <span className="field-label">DESCRIPTION:</span>
                    <div className="field-value">{selectedTool.description}</div>
                  </div>
                </div>
              </div>

              <div className="view-section">
                <h3 className="section-title">Technical Details</h3>
                <div className="info-fields">
                  <div className="info-field">
                    <span className="field-label">IMPLEMENTATION REFERENCE:</span>
                    <div className="field-value code-value">{selectedTool.backend_implementation_ref}</div>
                  </div>
                  {selectedTool.required_auth_service_name && (
                    <div className="info-field">
                      <span className="field-label">AUTH SERVICE:</span>
                      <div className="field-value">{selectedTool.required_auth_service_name}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="view-section">
                <h3 className="section-title">Metadata</h3>
                <div className="info-fields">
                  <div className="info-field">
                    <span className="field-label">TOOL ID:</span>
                    <div className="field-value">{selectedTool.tool_definition_id}</div>
                  </div>
                  <div className="info-field">
                    <span className="field-label">CREATED:</span>
                    <div className="field-value">{formatDate(selectedTool.created_at)}</div>
                  </div>
                  <div className="info-field">
                    <span className="field-label">LAST UPDATED:</span>
                    <div className="field-value">{formatDate(selectedTool.updated_at)}</div>
                  </div>
                </div>
              </div>

              <div className="view-section">
                <div className="section-header">
                  <h3 className="section-title">Parameters Schema</h3>
                  <button
                    className="copy-schema-btn"
                    onClick={() => copyToClipboard(JSON.stringify(selectedTool.parameters_json_schema, null, 2))}
                    title="Copy Schema"
                  >
                    Copy Schema
                  </button>
                </div>
                <div className="schema-display clean-schema">
                  <span className="schema-label">JSON Schema Definition</span>
                  <div className="json-content">
                    <pre>{JSON.stringify(selectedTool.parameters_json_schema, null, 2)}</pre>
                  </div>
                </div>
              </div>
            </div>

            <div className="view-actions clean-actions">
              <button className="close-modal-btn" onClick={() => setShowViewModal(false)}>
                Close
              </button>
              <button
                className="edit-tool-btn primary-btn"
                onClick={() => {
                  setShowViewModal(false);
                  openEditModal(selectedTool);
                }}
              >
                Edit Tool
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tool Modal */}
      {showCreateModal && (
        <div className="modal-overlay modern-modal" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content tool-form-modal enhanced-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header gradient-header">
              <div className="header-title-section">
                <div className="header-text">
                  <h2>Create Tool Definition</h2>
                </div>
              </div>
              <button className="close-modal modern-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreateTool} className="tool-form clean-form">
              <div className="form-grid">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tool Name *</label>
                    <input
                      type="text"
                      value={formData.tool_name}
                      onChange={(e) => setFormData({...formData, tool_name: e.target.value})}
                      required
                      className="form-input"
                      placeholder="Enter tool name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Auth Service</label>
                    <input
                      type="text"
                      value={formData.required_auth_service_name}
                      onChange={(e) => setFormData({...formData, required_auth_service_name: e.target.value})}
                      className="form-input"
                      placeholder="Optional auth service name"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    className="form-textarea"
                    placeholder="Describe what this tool does"
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Backend Implementation Reference *</label>
                  <input
                    type="text"
                    value={formData.backend_implementation_ref}
                    onChange={(e) => setFormData({...formData, backend_implementation_ref: e.target.value})}
                    required
                    className="form-input"
                    placeholder="e.g., functions.email_sender or api.weather.get_forecast"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Parameters JSON Schema *</label>
                  <textarea
                    value={formData.parameters_json_schema}
                    onChange={(e) => setFormData({...formData, parameters_json_schema: e.target.value})}
                    required
                    className="form-textarea json-input"
                    placeholder='{"type": "object", "properties": {"param1": {"type": "string", "description": "..."}}}'
                    rows="8"
                  />
                </div>
              </div>

              <div className="form-actions clean-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn primary-btn">
                  Create Tool
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tool Modal */}
      {showEditModal && selectedTool && (
        <div className="modal-overlay modern-modal" onClick={() => setShowEditModal(false)}>
          <div className="modal-content tool-view-modal enhanced-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header gradient-header">
              <div className="header-title-section">
                <div className="tool-icon">🔧</div>
                <div className="header-text">
                  <h2>Edit Tool</h2>
                  <p className="tool-name-subtitle">{selectedTool.tool_name}</p>
                </div>
              </div>
              <button className="close-modal modern-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>

            <form onSubmit={handleEditTool} className="tool-form clean-form">
              <div className="form-grid">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tool Name *</label>
                    <input
                      type="text"
                      value={formData.tool_name}
                      onChange={(e) => setFormData({...formData, tool_name: e.target.value})}
                      required
                      className="form-input"
                      placeholder="Enter tool name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Auth Service</label>
                    <input
                      type="text"
                      value={formData.required_auth_service_name}
                      onChange={(e) => setFormData({...formData, required_auth_service_name: e.target.value})}
                      className="form-input"
                      placeholder="Optional auth service name"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    className="form-textarea"
                    placeholder="Describe what this tool does"
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Backend Implementation Reference</label>
                  <input
                    type="text"
                    value={formData.backend_implementation_ref}
                    onChange={(e) => setFormData({...formData, backend_implementation_ref: e.target.value})}
                    required
                    className="form-input"
                    placeholder="e.g., netsuite_tool.create_bill_from_purchase_order"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Parameters JSON Schema</label>
                  <textarea
                    value={formData.parameters_json_schema}
                    onChange={(e) => setFormData({...formData, parameters_json_schema: e.target.value})}
                    className="form-textarea json-input"
                    placeholder="JSON schema for tool parameters"
                    rows="8"
                  />
                </div>
              </div>

              <div className="form-actions clean-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn primary-btn">
                  Update Tool
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </MainLayout>
  );
};

export default ToolsManagement;